import { TRUCK_DOCUMENT_CONTENT_TYPES } from '@chukua/common';
import { DatePicker, Form, Modal, Select, Upload } from 'antd';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import { UploadCloud } from 'lucide-react';
import { useState } from 'react';
import type { DocumentType } from '../reference-data/reference-data.api';
import type { TruckDocumentView } from './trucks.api';
import type { DocumentFormInput } from './use-truck-documents';

interface TruckDocumentModalProps {
  open: boolean;
  /** The document being edited, or null when attaching a new one. */
  document: TruckDocumentView | null;
  documentTypes: DocumentType[];
  submitting: boolean;
  onSubmit: (input: DocumentFormInput) => Promise<boolean>;
  onCancel: () => void;
}

interface FormValues {
  documentTypeId: string;
  expiryDate: Dayjs;
}

function uploadHint(file: File | null, editing: boolean): string {
  if (file) return file.name;
  return editing ? 'Drop a file to replace the current one, or leave empty to keep it' : 'Click or drag a PDF, JPEG or PNG';
}

/**
 * Attach a document, or edit an existing one — a new file is optional when editing (an
 * omitted file keeps the current one). Keyed by the parent on the document being edited,
 * so opening it for a different document remounts it with fresh state rather than an effect.
 */
export function TruckDocumentModal({ open, document, documentTypes, submitting, onSubmit, onCancel }: TruckDocumentModalProps) {
  const [form] = Form.useForm<FormValues>();
  const [file, setFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState(false);

  async function submit() {
    const values = await form.validateFields();
    if (!document && !file) {
      setFileError(true);
      return;
    }

    const submitted = await onSubmit({ documentTypeId: values.documentTypeId, expiryDate: values.expiryDate.format('YYYY-MM-DD'), ...(file ? { file } : {}) });
    if (submitted) setFile(null);
  }

  return (
    <Modal
      open={open}
      title={document ? 'Edit document' : 'Add document'}
      onOk={submit}
      onCancel={onCancel}
      confirmLoading={submitting}
      destroyOnClose
      okText={document ? 'Save' : 'Add'}
    >
      <Form<FormValues>
        form={form}
        layout="vertical"
        requiredMark={false}
        initialValues={{ documentTypeId: document?.documentTypeId, expiryDate: document ? dayjs(document.expiryDate) : undefined }}
      >
        <Form.Item label="File" required={!document}>
          <Upload.Dragger
            accept={TRUCK_DOCUMENT_CONTENT_TYPES.join(',')}
            multiple={false}
            maxCount={1}
            beforeUpload={(next) => {
              setFile(next);
              return false;
            }}
            onRemove={() => setFile(null)}
          >
            <p className="flex flex-col items-center gap-2 py-4 text-sm text-muted">
              <UploadCloud className="size-5" />
              {uploadHint(file, Boolean(document))}
            </p>
          </Upload.Dragger>
          {fileError && <p className="mt-1 text-sm text-error">A file is required</p>}
        </Form.Item>
        <Form.Item name="documentTypeId" label="Document type" rules={[{ required: true, message: 'Select a document type' }]}>
          <Select options={documentTypes.map((type) => ({ value: type.id, label: type.name }))} showSearch optionFilterProp="label" />
        </Form.Item>
        <Form.Item name="expiryDate" label="Expiry date" rules={[{ required: true, message: 'Select an expiry date' }]}>
          <DatePicker className="w-full" />
        </Form.Item>
      </Form>
    </Modal>
  );
}
